import { createFileRoute, getRouteApi, useNavigate, useRouter } from "@tanstack/react-router";
import {
    Button,
    Pagination,
    Link,
    Accordion,
    Tooltip,
    Spinner,
    Skeleton,
    Alert,
    Table,
    Separator,
} from "@heroui/react";
import { User } from "@repo/ui/components/User";
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import {
    AlertCircle,
    BadgeCheck,
    ChevronDown,
    ChevronRight,
    Clock,
    Cloud,
    CloudOff,
    Folder,
    HardDrive,
    InfoIcon,
    Layers,
    Layers2,
    MessageCircleWarning,
    Package,
    Package2,
    Pencil,
    ShieldBan,
    ShieldCheck,
    Trash2,
    XCircle,
} from "lucide-react";
import { useAuth } from "@repo/context/auth/auth";
import { ERROR_CODE } from "@repo/api";
import { LayerInfo, SendRecord, Template } from "@/types/template_trace";
import { toast } from "@heroui/react";
import { formatFileSizeSmart } from "@repo/utils/format";
import { DownloadModal } from "./-components/ComponentDownload";
import ComponentDynamicUser from "@repo/ui/pages/template_service/components/ComponentUser";
import { ComponentComfirm } from "@repo/ui/components/Modals";
import DeleteTemplateBlockedModal from "@repo/ui/pages/template_service/components/DeleteTemplateBlockedModal";
import StorageQuotaExhaustedAlert from "@repo/ui/pages/template_service/storage/StorageQuotaExhaustedAlert";
import { ComponentsUserWithHoverCard } from "@/components/ComponentsUser";
import { formatTime } from "@repo/utils/tools";
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { isRestricted } from "./-utils/utils";
import { asSearchString } from "@repo/utils/search-params";
import { useStorageQuota } from "@repo/ui/pages/template_service/storage/useStorageQuota";
import {
    useSendRecords,
    useTemplateDetail,
    useTemplateDetailMutations,
} from "@repo/ui/pages/template_service/detail/use-template-detail";
import { useUserProfile } from "@repo/query";

export type DetailSearch = {
    sendId?: string;
};

export const Route = createFileRoute("/template_protections/detail/$templateId")({
    validateSearch: (search: Record<string, unknown>): DetailSearch => ({
        sendId: asSearchString(search.sendId),
    }),
    component: TemplateDetailPage,
});

const detailRouteApi = getRouteApi("/template_protections/detail/$templateId");

//   import { useGetAppModal } from "@/hooks/useGetAppModal";

// 详情页骨架屏组件
const TemplateDetailSkeleton = () => {
    return (
        <div className="flex flex-col gap-2.5 items-start justify-start">
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-2.5 items-start justify-start md:p-2 sm:p-[10px] w-full">
                {/* 左侧预览区域骨架 */}
                <div className="flex flex-1 flex-col gap-4 items-start justify-start w-full lg:w-auto">
                    <div className="flex flex-col gap-2.5 items-start justify-start w-full">
                        {/* 模板标题和标签骨架 */}
                        <div className="flex flex-col gap-2.5 items-start justify-start w-full">
                            <Skeleton className="h-6 w-3/4 rounded-lg" />
                            <div className="flex flex-row gap-2.5 items-start justify-start flex-wrap">
                                <Skeleton className="h-6 w-16 rounded-lg" />
                                <Skeleton className="h-6 w-20 rounded-lg" />
                                <Skeleton className="h-6 w-24 rounded-lg" />
                            </div>
                            <Skeleton className="h-4 w-32 rounded" />
                        </div>
                        {/* 预览图片骨架 */}
                        <div className="flex flex-col gap-2.5 items-start justify-start w-full">
                            <Skeleton className="h-[300px] sm:h-[400px] lg:h-[573px] w-full rounded-lg" />
                        </div>
                    </div>
                </div>

                {/* 右侧详细信息区域骨架 */}
                <div className="flex-1 flex flex-col gap-6 sm:gap-8 items-start justify-start p-2 sm:p-[18px] w-full">
                    {/* 画师信息骨架 */}
                    <div className="flex sm:flex-row items-center justify-between w-full gap-3 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Skeleton className="h-12 w-12 rounded-full" />
                            <div className="flex flex-col gap-1">
                                <Skeleton className="h-4 w-24 rounded" />
                                <Skeleton className="h-3 w-16 rounded" />
                            </div>
                        </div>
                        <Skeleton className="h-8 w-20 rounded-full" />
                    </div>

                    {/* 模板说明骨架 */}
                    <div className="flex flex-col gap-2.5 items-start justify-start w-full">
                        <Skeleton className="h-4 w-full rounded" />
                        <Skeleton className="h-4 w-3/4 rounded" />
                        <Skeleton className="h-4 w-1/2 rounded" />
                    </div>

                    {/* 分割线 */}
                    <div className="w-full h-px bg-gray-200" />

                    {/* 文件信息骨架 */}
                    <div className="w-full">
                        <div className="flex flex-col gap-4 items-start justify-start w-full">
                            <Skeleton className="h-6 w-20 rounded" />
                            <div className="flex flex-col gap-2 w-full">
                                <Skeleton className="h-4 w-full rounded" />
                                <Skeleton className="h-4 w-full rounded" />
                                <Skeleton className="h-4 w-3/4 rounded" />
                                <Skeleton className="h-4 w-1/2 rounded" />
                            </div>
                        </div>
                    </div>

                    {/* 图层信息骨架 */}
                    <div className="w-full">
                        <div className="flex flex-col gap-4 items-start justify-start w-full">
                            <Skeleton className="h-6 w-20 rounded" />
                            <div className="flex flex-col gap-2 w-full">
                                <Skeleton className="h-4 w-full rounded" />
                                <Skeleton className="h-4 w-5/6 rounded" />
                                <Skeleton className="h-4 w-4/5 rounded" />
                            </div>
                        </div>
                    </div>

                    {/* 操作按钮骨架 */}
                    <div className="flex flex-row gap-3 items-center justify-start w-full">
                        <Skeleton className="h-10 w-24 rounded-full" />
                        <Skeleton className="h-10 w-20 rounded-full" />
                        <Skeleton className="h-10 w-28 rounded-full" />
                    </div>
                </div>
            </div>
        </div>
    );
};

// 参数行组件
const ParamRow = ({ label, value }: { label: string; value: string | React.ReactNode }) => (
    <div className="flex items-center justify-between px-2 py-1 w-full">
        <div className="flex-1 text-gray-500">{label}</div>
        <div className="flex-1 text-right">{value}</div>
    </div>
);

// 图层项组件
const LayerItem = ({
    layer,
    level = 0,
}: {
    layer: LayerInfo;
    level?: number;
}) => {
    const [isExpanded, setIsExpanded] = useState(level < 2); // 默认展开前两级
    const isGroup = layer.type === "group";
    const hasContent = isGroup && layer.content && layer.content.length > 0;

    return (
        <div className="w-full flex flex-col items-start justify-start px-2 hover:bg-gray-100 rounded transition-colors mb-1">
            <div
                className={`flex gap-2 items-center justify-start w-full py-1.5  ${isGroup && "cursor-pointer"}
                      }`}
                style={{ paddingLeft: `${level * 24}px` }}
                onClick={() => isGroup && setIsExpanded(!isExpanded)}
            >
                {/* 展开/收起图标 */}
                {isGroup && (
                    <div className="shrink-0">
                        {isExpanded ? (
                            <ChevronDown size={14} className="text-gray-500" />
                        ) : (
                            <ChevronRight size={14} className="text-gray-500" />
                        )}
                    </div>
                )}

                {/* 图层图标 */}
                <div className="shrink-0">
                    {isGroup ? (
                        <Folder size={16} className="text-gray-500" />
                    ) : (
                        <Layers2 size={16} className="text-gray-600" />
                    )}
                </div>

                {/* 图层名称 */}
                <div
                    className={`flex-1 text-sm text-gray-700 ${isGroup && "font-bold"}`}
                >
                    {layer.layer_title}
                </div>

                {/* 组内图层数量 */}
                {isGroup && hasContent && (
                    <div className="text-xs text-gray-400 shrink-0">
                        {layer.content.length} 项
                    </div>
                )}
            </div>

            {/* 子图层 */}
            {isGroup && isExpanded && hasContent && (
                <div className="w-full">
                    {layer.content.map((childLayer, index) => (
                        <LayerItem
                            key={`${layer.layer_title}-${index}`}
                            layer={childLayer}
                            level={level + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// 模板预览区域组件
const TemplatePreview = ({ template }: { template: Template }) => (
    <div className="flex flex-col gap-4 items-start justify-start w-full">
        <div className="flex flex-col gap-2.5 items-start justify-start w-full">
            <div className="font-bold text-lg flex flex-row gap-2.5 items-center justify-start flex-wrap">
                <span>{template.title}</span>
                <div className="flex flex-row gap-2.5 items-center justify-start">
                    {(template.certificate?.length ?? 0) > 0 ? (
                        <div className="bg-green-100 flex gap-1 items-center justify-start px-3 py-1 rounded-lg shrink-0 text-gray-500">
                            <BadgeCheck className="text-green-500" size={16} />
                            <div className="whitespace-nowrap text-xs text-green-500">
                                已上传购买凭证
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gray-100 flex gap-1 items-center justify-start px-3 py-1 rounded-lg shrink-0 text-gray-500">
                            <AlertCircle size={16} className="text-gray-500" />
                            <div className="whitespace-nowrap text-xs">未上传购买凭证</div>
                        </div>
                    )}
                    {template.file_num > 1 && (
                        <Tooltip>
                            <Tooltip.Trigger>
                                <div className="bg-purple-100 flex gap-1 items-center justify-start px-3 py-1 rounded-lg shrink-0 text-gray-500">
                                    <Package className="text-purple-500" size={16} />
                                    <div className="whitespace-nowrap text-xs text-purple-500">
                                        套装模板
                                    </div>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Content>该模板包含多个文件，当前仅展示其中一个文件</Tooltip.Content>
                        </Tooltip>
                    )}
                    {template.must_realname && (
                        <Tooltip>
                            <Tooltip.Trigger>
                                <div className="bg-red-100 flex gap-1 items-center justify-start px-3 py-1 rounded-lg shrink-0 text-gray-500">
                                    <ShieldCheck className="text-red-500" size={16} />
                                    <div className="whitespace-nowrap text-xs text-red-500">
                                        实名保护
                                    </div>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Content>签收该模板需先完成实名认证</Tooltip.Content>
                        </Tooltip>
                    )}
                    {template.saved && (
                        <div className="bg-blue-100 flex gap-1 items-center justify-start px-3 py-1 rounded-lg shrink-0 text-gray-500">
                            <Cloud size={16} className="text-blue-500" />
                            <div className="whitespace-nowrap text-xs text-blue-500">
                                永久保存
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="text-sm text-gray-500">模板编号#{template.id}</div>
        </div>
        <div className="flex flex-col gap-2.5 items-start justify-start w-full">
            <div
                className="h-[300px] sm:h-[400px] lg:h-[573px] rounded-lg w-full bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url('${template.preview_url || ''}')`,
                }}
            />
        </div>
    </div>
);

// 图层预览组件
const LayerPreview = ({ layers }: { layers: LayerInfo[] }) => {
    return (
        <div className="mb-4">
            {layers && layers.length > 0 && (
                <div className="flex flex-col items-start justify-start rounded-lg w-full max-h-[300px] overflow-y-auto">
                    {layers.map((layer, index) => (
                        <LayerItem key={`layer-${index}`} layer={layer} />
                    ))}
                </div>
            )}
            {(!layers || layers.length === 0) && (
                <div className="flex flex-col gap-2 items-center justify-center rounded-lg w-full text-gray-500">
                    <Layers size={24} />
                    <div className="text-sm">暂无图层信息</div>
                </div>
            )}
        </div>
    );
};
// 查询发货记录组件
export interface QuerySendRecordModalRef {
    refreshData: () => void;
}

const QuerySendRecordModal = forwardRef<
    QuerySendRecordModalRef,
    { templateId: string }
>(({ templateId }, ref) => {
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
    });
    const { data, isFetching, error, refetch } = useSendRecords(
        templateId,
        pagination.currentPage,
        pagination.pageSize,
    );
    const { retract } = useTemplateDetailMutations();
    const recordList = data?.content ?? [];
    const total = data?.total ?? 0;
    // 表格的加载态同时覆盖拉取记录与撤回中
    const retractLoading = isFetching || retract.isPending;

    useEffect(() => {
        if (error) {
            toast.danger((error as { msg?: string }).msg || "获取发货记录失败");
        }
    }, [error]);

    // 暴露刷新数据的方法给父组件
    useImperativeHandle(
        ref,
        () => ({
            refreshData: () => void refetch(),
        }),
        [refetch],
    );

    const handleRetract = useCallback((recordId: number) => {
        retract.mutate(recordId);
    }, [retract]);

    const columns = [
        {
            key: "user",
            label: "接收人",
        },
        {
            key: "is_received",
            label: "签收状态",
        },
        {
            key: "is_restricted",
            label: "帐号状态",
        },
        {
            key: "created_at",
            label: "发货时间",
        },
        {
            key: "received_at",
            label: "签收时间",
        },
        {
            key: "expire_at",
            label: "有效期至",
        },
        {
            key: "actions",
            label: "操作",
        },
    ];

    // 行渲染器
    const renderCell = useCallback((record: SendRecord, columnKey: React.Key) => {
        const cellValue = record[columnKey as keyof SendRecord];

        switch (columnKey) {
            case "user":
                return <ComponentDynamicUser uid={record.receiver_uid} />;
            case "is_received":
                return <span>{cellValue ? <span className="text-green-500">已签收</span> : <span className="text-gray-500">未签收</span>}</span>;
            case "is_restricted":
                return <span>{record.is_restricted ? <span className="text-red-500">被限制</span> : <span className="text-green-500">正常</span>}</span>;
            case "created_at":
            case "received_at":
            case "expire_at":
                // 时间戳为0显示"-"，否则格式化为YYYY-MM-DD HH:MM:SS
                const ts = Number(cellValue);
                const displayTime = !ts
                    ? "-"
                    : new Date(ts * 1000)
                        .toLocaleString("zh-CN", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                        })
                        .replace(/\//g, "-")
                        .replace(
                            /(\d{4})-(\d{2})-(\d{2}),\s?(\d{2}):(\d{2}):(\d{2})/,
                            "$1-$2-$3 $4:$5:$6",
                        );
                return <span>{displayTime}</span>;
            case "actions":
                return (
                    <ComponentComfirm
                        trigger={record.is_received ? <></> : <span className="text-primary cursor-pointer">撤回</span>}
                        title="撤回"
                        body={<div>对方尚未签收，撤回会导致对方无法签收，确认撤回吗</div>}
                        onConfirm={() => handleRetract(record.id)}
                        loading={retractLoading}
                    />
                );
            default:
                return cellValue;
        }
    }, [handleRetract, retractLoading]);

    const totalPages = Math.ceil(total / pagination.pageSize) || 1;

    return (
        <div className="flex flex-col gap-2.5 items-start justify-start bg-white rounded-lg p-6">
            <div className="font-bold text-lg">发货追踪</div>
            <div className="text-sm text-gray-500 mb-2">在此处可以查看所有发货记录，包括发货时间、签收时间、有效期等；也可以撤回没有签收的模板。</div>
            <Table className="w-full overflow-hidden" variant="secondary">
                <Table.ScrollContainer>
                    <Table.Content aria-label="发货记录">
                        <Table.Header columns={columns}>
                            {(column) => (
                                <Table.Column isRowHeader key={column.key}>{column.label}</Table.Column>
                            )}
                        </Table.Header>
                        <Table.Body
                            items={recordList}
                            renderEmptyState={() =>
                                retractLoading ? (
                                    <div className="py-8 flex items-center justify-center">
                                        <Spinner />
                                    </div>
                                ) : (
                                    <div>暂时还没有发货记录哦</div>
                                )
                            }
                        >
                            {(item: SendRecord) => (
                                <Table.Row id={String(item.id)}>
                                    {columns.map((col) => (
                                        <Table.Cell key={col.key}>
                                            {renderCell(item, col.key) as React.ReactNode}
                                        </Table.Cell>
                                    ))}
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table.Content>
                </Table.ScrollContainer>
            </Table>
            {totalPages > 1 ? (
                <div className="flex justify-center w-full">
                    <Pagination className="mx-auto" size="sm">
                        <Pagination.Content>
                            <Pagination.Item>
                                <Pagination.Previous
                                    isDisabled={pagination.currentPage <= 1}
                                    onPress={() =>
                                        setPagination((prev) => ({
                                            ...prev,
                                            currentPage: Math.max(1, prev.currentPage - 1),
                                        }))
                                    }
                                >
                                    <Pagination.PreviousIcon />
                                </Pagination.Previous>
                            </Pagination.Item>

                            {(() => {
                                const page = pagination.currentPage;
                                const pages: Array<number | "ellipsis"> = [];
                                const pushPage = (p: number) => {
                                    if (p >= 1 && p <= totalPages) pages.push(p);
                                };

                                if (totalPages <= 7) {
                                    for (let p = 1; p <= totalPages; p++) pushPage(p);
                                } else {
                                    pushPage(1);
                                    if (page > 3) pages.push("ellipsis");
                                    for (
                                        let p = Math.max(2, page - 1);
                                        p <= Math.min(totalPages - 1, page + 1);
                                        p++
                                    ) {
                                        pushPage(p);
                                    }
                                    if (page < totalPages - 2) pages.push("ellipsis");
                                    pushPage(totalPages);
                                }

                                return pages.map((p, idx) => (
                                    <Pagination.Item key={`${p}-${idx}`}>
                                        {p === "ellipsis" ? (
                                            <Pagination.Ellipsis />
                                        ) : (
                                            <Pagination.Link
                                                isActive={p === page}
                                                onPress={() =>
                                                    setPagination((prev) => ({
                                                        ...prev,
                                                        currentPage: p,
                                                    }))
                                                }
                                            >
                                                {p}
                                            </Pagination.Link>
                                        )}
                                    </Pagination.Item>
                                ));
                            })()}

                            <Pagination.Item>
                                <Pagination.Next
                                    isDisabled={pagination.currentPage >= totalPages}
                                    onPress={() =>
                                        setPagination((prev) => ({
                                            ...prev,
                                            currentPage: Math.min(
                                                totalPages,
                                                prev.currentPage + 1,
                                            ),
                                        }))
                                    }
                                >
                                    <Pagination.NextIcon />
                                </Pagination.Next>
                            </Pagination.Item>
                        </Pagination.Content>
                    </Pagination>
                </div>
            ) : null}
        </div>
    );
});

// 主组件
export default function TemplateDetailPage() {
    const { templateId } = detailRouteApi.useParams();
    const { sendId } = detailRouteApi.useSearch();
    const navigate = useNavigate();
    const router = useRouter();
    const { isAuthenticated, userInfo } = useAuth();
    const { isExhausted } = useStorageQuota();
    const queryRecordRef = useRef<QuerySendRecordModalRef>(null);
    // const { openGetAppModal } = useGetAppModal();
    const [deleteBlockedOpen, setDeleteBlockedOpen] = useState(false);

    // 只有在已认证时才加载模板数据
    const {
        data: template,
        isPending: templateLoading,
        error: templateError,
        noPermission,
    } = useTemplateDetail(templateId, sendId ?? undefined, isAuthenticated === true);
    const { data: artistInfo } = useUserProfile(template?.uid);
    const mutations = useTemplateDetailMutations();

    useEffect(() => {
        if (templateError && !noPermission) {
            toast.danger((templateError as { msg?: string }).msg || "获取模板详情失败");
        }
    }, [templateError, noPermission]);

    // const handleReport = () => {
    //     console.log("举报画师:", template?.uid);
    //     // 这里可以添加举报逻辑
    // };

    /** 下面的 handler 都返回 Promise 并在失败时 reject，ComponentComfirm 靠这个决定是否关闭弹窗 */
    const requireSendId = () => {
        const currentSendId = template?.send?.send_id;
        if (!currentSendId) {
            toast.danger("操作非法，请刷新页面重试");
            throw new Error("missing send_id");
        }
        return currentSendId;
    };

    const handleReceiveTemplate = () =>
        mutations.receive.mutateAsync(requireSendId()).then(() => undefined);

    const handleRetryMask = (send_id: string) => {
        if (!send_id) {
            toast.danger("数据异常，请刷新页面");
            return;
        }
        mutations.retryMask.mutate(send_id);
    };

    // 删除模板
    const handleDeleteTemplate = () => {
        if (!templateId) {
            toast.danger("操作非法，请刷新页面重试");
            return;
        }
        mutations.deleteTemplate
            .mutateAsync(templateId)
            .then(() => {
                // 延迟跳转，让用户看到成功提示
                setTimeout(() => {
                    router.history.back();
                }, 500);
            })
            .catch((error) => {
                if (error?.status === ERROR_CODE.HAS_UNRECEIVED_SENDS) {
                    setDeleteBlockedOpen(true);
                }
            });
    };

    // 转为永久存储
    const handleStorageReceiveTemplate = () =>
        mutations.storageReceive.mutateAsync(requireSendId()).then(() => undefined);

    // 从云空间删除永久存储
    const handleDeleteStorageReceiveTemplate = () =>
        mutations.deleteStorageReceive.mutateAsync(requireSendId()).then(() => undefined);

    const receiveLoading = mutations.receive.isPending;
    const retryMaskLoading = mutations.retryMask.isPending;
    const deleteLoading = mutations.deleteTemplate.isPending;
    const storageSaveLoading = mutations.storageReceive.isPending;
    const storageDeleteLoading = mutations.deleteStorageReceive.isPending;

    const isOwner = template?.uid === userInfo?.uid;

    if (noPermission) {
        return (
            <div className="flex flex-col items-center justify-center gap-8 min-h-dvh px-4">
                <span className="h-24 w-24 -mr-4" style={{ backgroundImage: "url('/images/results/treasy_error.png')", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.5 }}></span>
                <div className="text-lg font-bold">啊哦，您没有权限查看该模板哦</div>
                <span className="text-sm text-gray-500 text-center">
                    可能的原因：您不是该模板的签收方，或该模板因违规禁止访问
                </span>
                <Button onPress={() => router.history.back()}>返回上一页</Button>
            </div>
        );
    }

    return (
        <>
            {userInfo && isRestricted(userInfo) && (
                <Alert
                    key="restricted"
                    status="danger"
                    className="w-full sticky top-16 z-10 rounded-none"
                >
                    <Alert.Content>
                        <Alert.Description>
                            因违规，您已被禁止使用部分服务，请联系客服获取详情
                        </Alert.Description>
                    </Alert.Content>
                </Alert>
            )}
            <section className=" bg-gray-100 md:max-w-7xl mx-auto">
                <div className="px-4 sm:px-6 lg:px-11 py-6 sm:py-9">
                    <div className="bg-white rounded-lg p-4 sm:p-6 mb-6">
                        {templateLoading && <TemplateDetailSkeleton />}
                        {!templateLoading && template && (
                            <div className="flex flex-col gap-2.5 items-start justify-start">
                                <div className="flex flex-col lg:flex-row gap-4 lg:gap-2.5 items-start justify-start md:p-2 sm:p-[10px] w-full">
                                    {/* 左侧预览区域 */}
                                    <div className="flex flex-1 flex-col gap-4 items-start justify-start w-full lg:w-auto">
                                        <TemplatePreview template={template} />
                                    </div>

                                    {/* 右侧详细信息区域 */}
                                    <div className="flex-1 flex flex-col gap-6 sm:gap-8 items-start justify-start p-2 sm:p-[18px] w-full">
                                        {/* 画师信息 */}
                                        <div className="flex sm:flex-row items-center justify-between w-full gap-3 sm:gap-0">
                                            <ComponentsUserWithHoverCard
                                                userInfo={artistInfo}
                                                children={
                                                    <User
                                                        avatar_url={artistInfo?.avatar_url}
                                                        name={artistInfo?.nickname}
                                                        description={artistInfo?.uid}
                                                    />
                                                }
                                            />
                                            {!isOwner && (
                                                <Button
                                                    size="sm"
                                                    className="px-3 sm:px-4 py-2 rounded-full shrink-0"
                                                    onPress={() => toast("在线举报正在施工中，您可通过关于页面添加客服进行举报")}
                                                >
                                                    <MessageCircleWarning size={16} />
                                                    举报违规
                                                </Button>
                                            )}
                                        </div>
                                        {/* 模板说明 */}
                                        <div className="flex flex-col gap-2.5 items-start justify-start w-full">
                                            <div className="text-sm text-gray-800 whitespace-pre-line">
                                                {template.description || "对方暂时没有填写说明哦～"}
                                            </div>
                                        </div>
                                        <Separator className="bg-gray-50" orientation="horizontal" />
                                        <Accordion>
                                            <Accordion.Item
                                                id="file_info"
                                            >
                                                <Accordion.Heading>
                                                    <Accordion.Trigger>
                                                        <div className="flex flex-col items-start">
                                                            <span className="font-bold">文件信息</span>
                                                            <span className="text-sm text-gray-500 font-normal">模板文件元信息，如文件类型、文件大小、画布尺寸、图层数量等</span>
                                                        </div>
                                                        <Accordion.Indicator />
                                                    </Accordion.Trigger>
                                                </Accordion.Heading>
                                                <Accordion.Panel>
                                                    <Accordion.Body>
                                                        {/* 文件参数 */}
                                                        <div className="flex flex-col gap-2.5 items-start justify-start w-full">
                                                            <ParamRow
                                                                label="文件类型"
                                                                value={template.files?.[0]?.meta_info?.file_type || "未知"}
                                                            />
                                                            <ParamRow
                                                                label="文件大小"
                                                                value={<span className="inline-flex items-center gap-1">
                                                                    {formatFileSizeSmart(template.files?.[0]?.file_size ?? 0)}
                                                                    <Tooltip>
                                                                        <Tooltip.Trigger>
                                                                            <span className="inline-flex">
                                                                                <InfoIcon size={16} />
                                                                            </span>
                                                                        </Tooltip.Trigger>
                                                                        <Tooltip.Content>
                                                                            此处仅显示原始文件大小，实际打标后的文件大小可能会有所不同，仅供参考
                                                                        </Tooltip.Content>
                                                                    </Tooltip>
                                                                </span>}
                                                            />
                                                            <ParamRow
                                                                label="画布尺寸(px)"
                                                                value={
                                                                    template.files?.[0]?.meta_info?.pixel
                                                                        ? template.files?.[0]?.meta_info?.pixel.join(" x ")
                                                                        : "暂无数据"
                                                                }
                                                            />
                                                            <ParamRow
                                                                label="图层数量"
                                                                value={`${template.files?.[0]?.meta_info?.layer_num ?? 0} 个`}
                                                            />
                                                        </div>
                                                    </Accordion.Body>
                                                </Accordion.Panel>
                                            </Accordion.Item>

                                            <Accordion.Item
                                                id="layer_preview"
                                            >
                                                <Accordion.Heading>
                                                    <Accordion.Trigger>
                                                        <div className="flex flex-col items-start">
                                                            <span className="font-bold">图层预览</span>
                                                            <span className="text-sm text-gray-500 font-normal">查看模板文件内的图层信息</span>
                                                        </div>
                                                        <Accordion.Indicator />
                                                    </Accordion.Trigger>
                                                </Accordion.Heading>
                                                <Accordion.Panel>
                                                    <Accordion.Body>
                                                        {/* 图层预览 */}
                                                        <LayerPreview
                                                            layers={template.files[0]?.meta_info?.layer_info || []}
                                                        />
                                                    </Accordion.Body>
                                                </Accordion.Panel>
                                            </Accordion.Item>

                                            <Accordion.Item
                                                id="template_certificate"
                                                isDisabled={(template.certificate?.length ?? 0) === 0}
                                            >
                                                <Accordion.Heading>
                                                    <Accordion.Trigger>
                                                        <div className="flex flex-col items-start">
                                                            <span className="font-bold flex items-center gap-2">
                                                                购买凭证
                                                                {(template.certificate?.length ?? 0) > 0 ? <span className="text-green-500 text-xs">{template.certificate?.length ?? 0} 个</span> : <span className="text-red-500 text-xs">未提供</span>}
                                                            </span>
                                                            <span className="text-sm text-gray-500 font-normal">发货方提供的购买凭证，方便签收方查验真实性</span>
                                                        </div>
                                                        <Accordion.Indicator />
                                                    </Accordion.Trigger>
                                                </Accordion.Heading>
                                                <Accordion.Panel>
                                                    <Accordion.Body>
                                                        {/* 模板说明 */}
                                                        <div className="grid grid-cols-4 gap-4 w-full">
                                                            <PhotoProvider>
                                                                {(template.certificate || []).map((file) => (
                                                                    <PhotoView key={file} src={file}>
                                                                        <img width={128} height={128} className="rounded-lg cursor-pointer hover:scale-105 transition-all duration-300 w-full h-full object-cover" src={file} alt="" />
                                                                    </PhotoView>
                                                                ))}
                                                            </PhotoProvider>
                                                        </div>
                                                    </Accordion.Body>
                                                </Accordion.Panel>
                                            </Accordion.Item>

                                            {/* 订单信息 */}
                                            {!(template.uid === userInfo?.uid || template.send?.received_at === 0) && (
                                                <Accordion.Item
                                                    id="order_info"
                                                >
                                                    <Accordion.Heading>
                                                        <Accordion.Trigger>
                                                            <div className="flex flex-col items-start">
                                                                <span className="font-bold">订单信息</span>
                                                                <span className="text-sm text-gray-500 font-normal">签收后的相关订单信息</span>
                                                            </div>
                                                            <Accordion.Indicator />
                                                        </Accordion.Trigger>
                                                    </Accordion.Heading>
                                                    <Accordion.Panel>
                                                        <Accordion.Body>
                                                            <div className="flex flex-col gap-2.5 items-start justify-start w-full">
                                                                <ParamRow
                                                                    label="订单编号"
                                                                    value={template.send?.send_id}
                                                                />
                                                                <ParamRow
                                                                    label="签收时间"
                                                                    value={formatTime(
                                                                        template.send?.received_at,
                                                                        "YYYY-MM-DD HH:mm:ss",
                                                                    )}
                                                                />
                                                                <ParamRow
                                                                    label="有效期至"
                                                                    value={formatTime(
                                                                        template.send?.expire_at,
                                                                        "YYYY-MM-DD HH:mm:ss",
                                                                    )}
                                                                />
                                                            </div>
                                                        </Accordion.Body>
                                                    </Accordion.Panel>
                                                </Accordion.Item>
                                            )}
                                        </Accordion>

                                        {/* 警告提示 */}
                                        {!isOwner && !template.saved && template.send?.expire_at > Date.now() / 1000 && (
                                            <div className="bg-red-50 flex gap-2.5 justify-start p-3 rounded-lg w-full items-center text-red-500">
                                                <Clock size={16} />
                                                <div className="flex-1 leading-relaxed text-sm">
                                                    模板有效期至{" "}
                                                    {formatTime(
                                                        template.send?.expire_at,
                                                        "YYYY-MM-DD HH:mm:ss",
                                                    )}
                                                    ，请尽快下载保存以免失效
                                                </div>
                                            </div>
                                        )}

                                        {template.send?.received_at && !template.saved && template.send?.expire_at < Date.now() / 1000 && (
                                            <div className="flex gap-2.5 justify-start p-3 rounded-lg w-full items-center text-gray-500">
                                                <Clock size={16} />
                                                <div className="flex-1 leading-relaxed text-sm">
                                                    模板已超过保存期限无法下载。如有疑问，请联系发货人获取进一步协助。
                                                </div>
                                            </div>
                                        )}

                                        {/* 失败提示 */}
                                        {!isOwner && template.send?.received_at && template.send?.mask_status === 2 && (
                                            <div className="bg-red-50 flex gap-2.5 justify-start p-3 rounded-lg w-full items-center text-red-500">
                                                <XCircle size={16} />
                                                <div className="leading-relaxed text-sm items-center">
                                                    我们检测到打标出现了异常，您可
                                                    <Link className="text-sm text-red-500 font-bold underline underline-offset-4 cursor-pointer"
                                                        onPress={() => { handleRetryMask(template.send?.send_id) }}>
                                                        点此重试打标</Link>
                                                </div>
                                                {retryMaskLoading && <Spinner size="sm" />}
                                            </div>
                                        )}

                                        {!isOwner && template.send?.received_at && template.send?.mask_status === 0 && (
                                            <>
                                                <div className="bg-gray-50 flex gap-2.5 justify-start p-3 rounded-lg w-full items-center text-gray-500">
                                                    <Spinner size="sm" />
                                                    <div className="flex-1 leading-relaxed text-sm">
                                                        我们仍在准备您的模板，准备的时长取决于模板的复杂程度，请稍后再来查看吧～
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {!isOwner && !template.send?.received_at && (
                                            <>
                                                <div className="bg-gray-100 flex gap-2.5 justify-start p-3 rounded-lg w-full items-center text-gray-500">
                                                    <AlertCircle size={16} />
                                                    <div className="flex-1 leading-relaxed text-sm">
                                                        您还未签收模板，请核对模板内容，并点击下方的“确认签收”来签收模板；如果模板和预期的内容不一致，请勿签收并请联系发货人撤回模板。
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {isOwner && (template.certificate?.length ?? 0) === 0 && (
                                            <div className="bg-[#fffaea] flex gap-2.5 justify-start p-3 rounded-lg w-full items-center text-orange-500">
                                                <AlertCircle size={16} />
                                                <div className="flex-1 leading-relaxed text-sm">
                                                    您未上传购买凭证，建议您上传购买凭证，提升买家信任度
                                                </div>
                                                <Link
                                                    className={"cursor-pointer text-sm text-orange-500"}
                                                    onPress={() =>
                                                        navigate({ to: "/template_protections/edit/$templateId", params: { templateId } })
                                                    }
                                                >
                                                    去上传<ChevronRight size={14} />
                                                </Link>
                                            </div>
                                        )}

                                        {(!isOwner) && (template.must_realname && !userInfo?.real_id.valid) && (
                                            <div className="bg-red-100 flex gap-2.5 justify-start p-3 rounded-lg w-full items-center text-red-500">
                                                <ShieldBan size={16} />
                                                <div className="flex-1 leading-relaxed text-sm">
                                                    对方开启了实名保护，签收模板前您需要先完成实名认证
                                                </div>
                                                {/* <Link
                                                    className={"cursor-pointer text-sm text-red-500"}
                                                    onPress={() => openGetAppModal(true)}
                                                >
                                                    去实名 <ChevronRight size={14} />
                                                </Link> */}
                                            </div>
                                        )}

                                        {!isOwner &&
                                            template.send?.received_at &&
                                            template.send?.expire_at > Date.now() / 1000 &&
                                            !template.saved &&
                                            isExhausted && (
                                                <StorageQuotaExhaustedAlert
                                                    className="w-full rounded-md"
                                                    storageManageHref="/template_protections/storage"
                                                />
                                            )}

                                        {/* 操作区*/}
                                        <div className="grid grid-cols-2 lg:grid-cols-3 flex-wrap lg:justify-start w-full gap-4">
                                            {isOwner && templateId && (
                                                <>
                                                    <Button
                                                        fullWidth
                                                        className="col-span-2 md:col-span-1"
                                                        onPress={() => navigate({ to: "/template_protections/send", search: { templateId, templateTitle: template?.title, templatePreviewUrl: template?.preview_url } })}
                                                    >
                                                        <Package size={18} />
                                                        <span>发货</span>
                                                    </Button>
                                                    <Button
                                                        fullWidth
                                                        className="col-span-1"
                                                        variant="tertiary"
                                                        onPress={() =>
                                                            navigate({ to: "/template_protections/edit/$templateId", params: { templateId } })
                                                        }
                                                    >
                                                        <Pencil size={18} />
                                                        <span>编辑</span>
                                                    </Button>
                                                    <ComponentComfirm
                                                        buttonTitle={
                                                            <span className="flex items-center gap-2">
                                                                <Trash2 size={18} />
                                                                删除
                                                            </span>
                                                        }
                                                        buttonProps={{
                                                            className: "col-span-1",
                                                            fullWidth: true,
                                                            variant: "danger-soft",
                                                        }}
                                                        title="确认删除"
                                                        body={
                                                            <div className="flex flex-col gap-3">
                                                                <p className="text-gray-800">
                                                                    您确定要删除模板 <span className="font-bold text-red-600">「{template?.title}」</span> 吗？
                                                                </p>
                                                                <p className="text-sm text-red-600">
                                                                    删除后您将无法再使用该模板发货，已发货的模板将不受影响
                                                                </p>
                                                            </div>
                                                        }
                                                        onConfirm={handleDeleteTemplate}
                                                        loading={deleteLoading}
                                                    />
                                                </>
                                            )}

                                            {!isOwner && template.send?.received_at && (template.saved || template.send?.expire_at > Date.now() / 1000) && (
                                                <DownloadModal
                                                    SendId={template.send?.send_id}
                                                    BtnProps={{
                                                        fullWidth: true,
                                                        className: "col-span-1",
                                                        isDisabled: template.send?.mask_status === 0 || template.send?.mask_status === 2,
                                                    }}
                                                />
                                            )}
                                            {!isOwner &&
                                                template.send?.received_at &&
                                                template.send?.expire_at > Date.now() / 1000 &&
                                                !template.saved && (
                                                    <ComponentComfirm
                                                        buttonTitle={
                                                            <span className="flex items-center gap-2">
                                                                <HardDrive size={18} />
                                                                转存至云空间
                                                            </span>
                                                        }
                                                        buttonProps={{
                                                            fullWidth: true,
                                                            variant: "tertiary",
                                                            className: "col-span-1",
                                                            isDisabled: isExhausted || template.send?.mask_status === 0 || template.send?.mask_status === 2,
                                                        }}
                                                        title="转存至云空间"
                                                        body={
                                                            <div>
                                                                转移后，该文件将永久有效，但是会占用您的兽聚汇云空间，是否继续？
                                                            </div>
                                                        }
                                                        onConfirm={handleStorageReceiveTemplate}
                                                        loading={storageSaveLoading}
                                                    />
                                                )}
                                            {!isOwner && template.saved && (
                                                <ComponentComfirm
                                                    buttonTitle={
                                                        <span className="flex items-center gap-2">
                                                            <CloudOff size={18} />
                                                            从云空间中删除
                                                        </span>
                                                    }
                                                    buttonProps={{
                                                        fullWidth: true,
                                                        variant: "danger-soft",
                                                        className: "col-span-1",
                                                    }}
                                                    title="确认删除"
                                                    body={<div>删除后，该文件将从云空间中移除且无法再下载，占用的空间将会被释放，确认移除吗？</div>}
                                                    onConfirm={handleDeleteStorageReceiveTemplate}
                                                    loading={storageDeleteLoading}
                                                />
                                            )}
                                            {!isOwner && !template.send?.received_at && (
                                                <ComponentComfirm
                                                    buttonTitle={
                                                        <span className="flex items-center gap-2">
                                                            <Package2 size={18} />
                                                            签收模板
                                                        </span>
                                                    }
                                                    buttonProps={{
                                                        fullWidth: true,
                                                        className: "col-span-2",
                                                        isDisabled: (template.must_realname && !userInfo?.real_id.valid) || (userInfo ? isRestricted(userInfo) : false),
                                                    }}
                                                    title="确认签收"
                                                    body={
                                                        <div>
                                                            请仔细核对模板内容，如果模板和预期的内容不一致，请勿签收并请联系发货人撤回模板；
                                                            <br /> <br />
                                                            <span className="text-red-500">一旦签收将不可撤回，请确认！</span>
                                                        </div>
                                                    }
                                                    onConfirm={handleReceiveTemplate}
                                                    loading={receiveLoading}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {isOwner && templateId && (
                        <QuerySendRecordModal ref={queryRecordRef} templateId={templateId} />
                    )}
                    {templateId && (
                        <DeleteTemplateBlockedModal
                            isOpen={deleteBlockedOpen}
                            onOpenChange={setDeleteBlockedOpen}
                            templateId={templateId}
                            onDeleted={() => {
                                setTimeout(() => {
                                    router.history.back();
                                }, 500);
                            }}
                        />
                    )}
                </div>
            </section >
        </>
    );
}
